/**
 * Типы биллинга/фолио (#338) — счёт гостя внутри отеля.
 *
 * Бизнес-модель (решение владельца, сверено с ТЗ v3.4 §«оплата-как-статус»):
 * гость НЕ платит онлайн — отель ведёт внутренний счёт (фолио) на гостя /
 * проживание. Персонал (reception/staff/менеджер) добавляет позиции после
 * рум-сервиса/при выезде и отмечает их оплаченными на стойке. Гость только
 * СМОТРИТ свой счёт: позиции, статусы и общий неоплаченный итог.
 *
 * Контракт ТОЧНО соответствует Go-DTO internal/billing/http_app/dto.go:
 *   GET   /v1/me/folio                       — гость: свои фолио + total_unpaid
 *   GET   /v1/folios                         — персонал: все фолио тенанта
 *   GET   /v1/folios/{id}                    — персонал: детали + позиции
 *   POST  /v1/folios/{id}/line-items         — персонал: добавить позицию
 *   PATCH /v1/folios/{id}/line-items/{lid}   — персонал: сторнировать позицию
 *   POST  /v1/folios/{id}/settlements        — персонал: записать оплату
 *   PATCH /v1/folios/{id}                     — персонал: статус (settled/closed)
 *
 * Суммы — десятичные NUMERIC из БД (рубли, не копейки), как отдаёт бэк.
 */

/** Виды позиций фолио (folio_line_items.kind). */
export type FolioLineKind =
  | 'room_charge'
  | 'food'
  | 'beverage'
  | 'spa'
  | 'beach'
  | 'vendor'
  | 'minibar'
  | 'damage'
  | 'adjustment'
  | 'tax'
  | 'service_charge'
  | 'tip'
  | 'refund'
  | 'other';

/** Статусы фолио (folios.status). */
export type FolioStatus =
  | 'open'
  | 'partially_settled'
  | 'settled'
  | 'closed'
  | 'disputed'
  | 'written_off';

/** Позиция счёта (folio_line_items). */
export interface FolioLineItem {
  id: string;
  kind: FolioLineKind | string;
  description: string;
  qty: number;
  unit_price: number;
  total: number;
  currency: string;
  posted_at: string;
  /** Сторнирована ли позиция (reversed_at != null). */
  reversed: boolean;
  source_request_id?: string;
}

/** Сводка фолио (список персонала). */
export interface FolioSummary {
  id: string;
  stay_id?: string;
  guest_id?: string;
  status: FolioStatus | string;
  currency: string;
  total_charges: number;
  total_payments: number;
  balance: number;
  /** Неоплаченный остаток (>= 0, для отображения). */
  unpaid: number;
  opened_at: string;
}

/** Детали фолио = сводка + позиции. */
export interface FolioDetail extends FolioSummary {
  lines: FolioLineItem[];
}

/** Ответ списка фолио (персонал/менеджер). */
export interface FolioListResponse {
  items: FolioSummary[];
  count: number;
}

/** Ответ гостевого фолио: его счета + общий неоплаченный итог. */
export interface MyFolioResponse {
  folios: FolioDetail[];
  total_unpaid: number;
  currency: string;
  count: number;
}

/** Тело POST /v1/folios/{id}/line-items — персонал добавляет позицию. */
export interface AddFolioLineInput {
  kind: FolioLineKind | string;
  description: string;
  qty: number;
  unit_price: number;
  source_request_id?: string;
}

/** Тело POST /v1/folios/{id}/settlements — персонал записывает оплату. */
export interface RecordSettlementInput {
  amount: number;
  notes?: string;
}

/** Тело PATCH /v1/folios/{id} — персонал меняет статус фолио. */
export interface SetFolioStatusInput {
  status: FolioStatus | string;
}

/** Человекочитаемые подписи статусов фолио (RU). */
export const FOLIO_STATUS_LABELS: Readonly<Record<string, string>> = {
  open: 'Открыт',
  partially_settled: 'Оплачен частично',
  settled: 'Оплачен',
  closed: 'Закрыт',
  disputed: 'Спор',
  written_off: 'Списан',
};

/** Человекочитаемые подписи видов позиций (RU). */
export const FOLIO_KIND_LABELS: Readonly<Record<string, string>> = {
  room_charge: 'Проживание',
  food: 'Еда',
  beverage: 'Напитки',
  spa: 'Спа',
  beach: 'Пляж',
  vendor: 'Партнёр',
  minibar: 'Минибар',
  damage: 'Ущерб',
  adjustment: 'Корректировка',
  tax: 'Налог',
  service_charge: 'Сервисный сбор',
  tip: 'Чаевые',
  refund: 'Возврат',
  other: 'Прочее',
};
